import React, { useState, useEffect } from 'react';
import { Modal, Form, Upload, Input, Checkbox, Card, Space, DatePicker, Select, Button } from 'antd';
import {
  InboxOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
  UploadOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { toast } from 'react-toastify';

import api from '@services/api';
import { ApiError, DeviceType, FirmwareSummary } from '@services/types';
import { UploadChangeParam } from 'antd/lib/upload';
import { Tabs } from 'antd/es';
import { FirmwareS3Request, UploadFirmwareWithUiFormRequest } from '@openapi';
import { Navigation } from '@constants/Navigation';
import { useHistory } from 'react-router-dom';
import { getUserRoleByRoles, selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const typeArray = ['fw', 'ui', 'loader', 'custofw1', 'custofw2', 'custofw3'];

const CreateFirmwareModal = () => {
  const { user } = React.useContext(AppContext);
  const [form] = Form.useForm<{ file: Blob }>();
  const [formS3] = Form.useForm<FirmwareS3Request>();
  const history = useHistory();
  const [formS3Local] = Form.useForm<UploadFirmwareWithUiFormRequest>();
  const [file, setFile] = React.useState<Blob>();
  const [firmware, setFirmware] = useState<any>();
  const [summaryModule, setSummaryModule] = React.useState(false);
  const [tabChange, setTabChange] = React.useState<string>('local');
  const [isByCredentials, setIsByCredentials] = React.useState(false);
  const [errorUpload, setErrorUpload] = React.useState<FirmwareSummary['etc']>();
  const [uploadMessage, setUploadMessage] = React.useState('');
  const [icon, setIcon] = React.useState('');
  const [enableInvalidationLocal, setEnableInvalidationLocal] = React.useState<boolean>(false);
  const [enableInvalidation, setEnableInvalidation] = React.useState<boolean>(false);
  const [extraData, setExtraData] = React.useState<boolean>(false);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);

  const normFile = (e: UploadChangeParam) => {
    if (Array.isArray(e)) {
      return e;
    }
    if (e.fileList.length > 1) {
      e.fileList.shift();
    }
    return e && e.fileList;
  };

  const role: string | undefined = getUserRoleByRoles(user?.roles || []);

  const handleFileChange = (options: UploadRequestOption) => {
    if (typeof options.onSuccess === 'function') {
      options.onSuccess('ok');
    }
    setFile(options.file as Blob);
    return Promise.resolve('11');
  };
  const onClose = () => {
    history.push(Navigation.Firmwares);
  };

  const handleSubmit = async () => {
    const value = await formS3.validateFields();
    try {
      if (tabChange === 's3') {
        await api.firmwares.deployFirmwareS3({
          firmwareS3Request: value,
          enableInvalidation: enableInvalidation!,
        });
      } else if (tabChange === 'local') {
        setUploadMessage('Wait, the file is validating and uploading');
        await form.validateFields();
        if (!file) {
          throw new Error('File not found');
        }
        await api.firmwares
          .createFirmware({ file: file, enableInvalidation: enableInvalidationLocal! })
          .then((data) => {
            setErrorUpload(data.etc);
          });
        setSummaryModule(!summaryModule);
      } else if (tabChange === 's3Form') {
        setUploadMessage('Wait, the file is validating and uploading');
        const valueForm: any = await formS3Local.validateFields();
        const toStringValues: any = {};
        for (const key in valueForm) {
          if (key === 'firmwareBinary' && file) {
            toStringValues[key] = file;
          } else if (key === 'enableInvalidation') {
            toStringValues[key] = valueForm[key];
          } else if (key === 'date' && valueForm[key]) {
            toStringValues[key] = valueForm[key].toISOString();
          } else if (key === 'extraData' && valueForm[key]) {
            toStringValues[key] = JSON.stringify(valueForm[key]);
          } else {
            toStringValues[key] = String(valueForm[key]);
          }
        }
        await api.firmwares.createS3FormFirmware(toStringValues);
      }
      form.resetFields();
      setUploadMessage('');
      toast.success('Firmware was created successfully');
      onClose();
    } catch (response) {
      setUploadMessage('');
      if (response instanceof ApiError) {
        setErrorUpload(response.error.etc);
        form.resetFields();
        for (const value in response.error.propertyErrors) {
          if (value === 'primaryDeviceType') {
            setIcon('usageId');
          } else {
            setIcon(value);
          }
        }
        toast.error(`${response.error.details || Object.values(response.error.propertyErrors!).map((item) => item)}`);
        onClose();
        setSummaryModule(response.error.details ? false : !summaryModule);
      } else {
        toast.error('Something went wrong');
      }
    }
  };

  useEffect(() => {
    api.administration.getDeviceTypes().then((data) => {
      setDeviceTypes(data);
    });
  }, []);

  return (
    <>
      <Card
        title="Add new firmware"
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
              Back to Firmwares Page
            </Button>
            <Button type="primary" onClick={handleSubmit}>
              Create
            </Button>
          </Space>
        }
      >
        <Tabs
          onChange={(key) => {
            setTabChange(key);
          }}
          type="card"
          size="small"
          defaultActiveKey={tabChange}
        >
          <TabPane tab={'Local'} key="local">
            <Form form={form} layout="vertical" name="form_in_modal">
              <Form.Item name="enableInvalidation" valuePropName="checked">
                <Checkbox onChange={() => setEnableInvalidationLocal(!enableInvalidationLocal)}>Invalidation</Checkbox>
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please choose the file',
                  },
                ]}
                name="dragger"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                noStyle
              >
                <Upload.Dragger name="file" accept=".tar.gz,.tgz" customRequest={handleFileChange} maxCount={0}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">.tar.gz or .tgz</p>
                </Upload.Dragger>
              </Form.Item>
              {uploadMessage && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content max-content',
                    gridGap: '10px',
                    alignItems: 'center',
                    paddingTop: '5px',
                  }}
                >
                  <span>{uploadMessage}</span>
                  <LoadingOutlined />
                </div>
              )}
            </Form>
          </TabPane>
          <TabPane tab={'S3'} key="s3">
            <Form
              form={formS3}
              layout="vertical"
              name="form_in_modal"
              initialValues={{
                s3Url: '',
                isByCredentials: true,
                accessKey: '',
                secretKey: '',
              }}
            >
              <Form.Item name="enableInvalidation" valuePropName="checked">
                <Checkbox onChange={() => setEnableInvalidation(!enableInvalidation)}>Invalidation</Checkbox>
              </Form.Item>
              <Form.Item name="isByCredentials" valuePropName="checked">
                <Checkbox onChange={() => setIsByCredentials(!isByCredentials)}>Use default credentials</Checkbox>
              </Form.Item>
              <Form.Item
                name="s3Url"
                label="S3 URL"
                rules={[
                  {
                    required: true,
                    message: 'Please input the s3Url',
                  },
                ]}
              >
                <Input placeholder={' https://s3.region-code.amazonaws.com/bucket-name/path/firmware-package.tar.gz'} />
              </Form.Item>
              {isByCredentials && (
                <>
                  <Form.Item name="accessKey" label="Access Key">
                    <Input />
                  </Form.Item>
                  <Form.Item name="secretKey" label="Secret">
                    <Input />
                  </Form.Item>
                </>
              )}
            </Form>
          </TabPane>
          <TabPane disabled={role === 'PARTNER'} tab="S3 Form" key="s3Form">
            <Form
              form={formS3Local}
              layout="vertical"
              name="form_in_modal"
              initialValues={{
                enableInvalidation: false,
                extraData: [{}],
              }}
            >
              <Form.Item name="enableInvalidation" valuePropName="checked">
                <Checkbox>Invalidation</Checkbox>
              </Form.Item>
              <Form.Item
                name="firmwareBinary"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                noStyle
                rules={[
                  {
                    required: true,
                    message: 'Please choose the file',
                  },
                ]}
              >
                <Upload name="file" customRequest={handleFileChange} maxCount={0}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                style={{ marginTop: '10px' }}
                name="version"
                label="Version"
                rules={[
                  {
                    required: true,
                    message: 'Please enter a version',
                  },
                ]}
              >
                <Input placeholder="0.0.0" />
              </Form.Item>
              <Form.Item
                name="type"
                label="Type"
                rules={[
                  {
                    required: true,
                    message: 'Please select the type',
                  },
                ]}
              >
                <Select allowClear showSearch filterOption={selectSearchFilter}>
                  {typeArray.map((item, index) => (
                    <Option key={index} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="date"
                label="Date"
                rules={[
                  {
                    required: true,
                    message: 'Please choose the date',
                  },
                ]}
              >
                <DatePicker showTime format="YYYY-MM-DDTHH:mm:ss.SSSZ" placeholder="Select date and time" />
              </Form.Item>
              <Form.Item
                name="deviceTypeIds"
                label="Device Type"
                rules={[
                  {
                    required: true,
                    message: 'Select a device type',
                  },
                ]}
              >
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {deviceTypes.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.deviceName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="client"
                label="Client"
                rules={[
                  {
                    required: true,
                    message: 'Please enter a name',
                  },
                ]}
              >
                <Input placeholder="Name" />
              </Form.Item>
              <Form.Item
                name="comment"
                label="Comment"
                rules={[
                  {
                    required: true,
                    message: 'Leave a comment',
                  },
                ]}
              >
                <TextArea rows={4} placeholder="Your notes" />
              </Form.Item>
              <Checkbox
                style={{ marginBottom: '10px' }}
                onClick={() => {
                  setExtraData(!extraData);
                }}
              >
                Extra Data
              </Checkbox>
              {extraData && (
                <Form.List name="extraData">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Space
                          key={key}
                          style={{
                            display: 'flex',
                            marginBottom: 8,
                          }}
                          align="baseline"
                        >
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            rules={[
                              {
                                required: true,
                                message: 'Missing name',
                              },
                            ]}
                          >
                            <Input placeholder="Name" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'value']}
                            rules={[
                              {
                                required: true,
                                message: 'Missing value',
                              },
                            ]}
                          >
                            <Input placeholder="Value" />
                          </Form.Item>
                          {index > 0 && <MinusCircleOutlined onClick={() => remove(name)} />}
                        </Space>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          Add field
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              )}
              {uploadMessage && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content max-content',
                    gridGap: '10px',
                    alignItems: 'center',
                    paddingTop: '5px',
                  }}
                >
                  <span>{uploadMessage}</span>
                  <LoadingOutlined />
                </div>
              )}
            </Form>
          </TabPane>
        </Tabs>
      </Card>
      <Modal
        width="700px"
        visible={summaryModule}
        title="Summary"
        okText="OK"
        cancelText="Cancel"
        onOk={() => setSummaryModule(!summaryModule)}
        onCancel={() => setSummaryModule(!summaryModule)}
      >
        {Object.entries(errorUpload?.descriptionFile || [])?.map(([key, value]) => (
          <div
            style={{
              padding: '15px',
              display: 'grid',
              gridTemplateColumns: '250px 250px 200px',
              alignItems: 'center',
            }}
            key={key}
          >
            <span style={{ textTransform: 'capitalize', fontWeight: '500', fontSize: '17px' }}>{key}:</span>
            <span style={{ fontWeight: '400' }}>{value?.toString()}</span>
            {key === icon ? (
              <CloseCircleFilled style={{ color: '#B60606FF', fontSize: '20px' }} />
            ) : (
              <CheckCircleFilled style={{ color: '#18b606', fontSize: '20px' }} />
            )}
          </div>
        ))}
      </Modal>
    </>
  );
};

export default React.memo(CreateFirmwareModal);
